import { Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { SiteFooter } from "@/components/SiteFooter";
import { FadeUp } from "@/components/FadeUp";
import { SeamDivider } from "@/components/SeamDivider";
import { BaseballCursor } from "@/components/BaseballCursor";

const PRICING = [
  {
    plan: "weekly_1_team",
    label: "1 team / weekly",
    price: "$1.99",
    cadence: "per week",
    desc: "Easy in, easy out. Cancel anytime.",
    cadenceKey: "weekly",
  },
  {
    plan: "seasonal_1_team",
    label: "1 team / seasonal",
    price: "$25",
    cadence: "once",
    desc: "Rest of the regular season plus the entire postseason.",
    cadenceKey: "seasonal",
  },
  {
    plan: "weekly_3_team",
    label: "3 teams / weekly",
    price: "$3.69",
    cadence: "per week",
    desc: "Three teams. Pay by the week.",
    cadenceKey: "weekly",
  },
  {
    plan: "seasonal_3_team",
    label: "3 teams / seasonal",
    price: "$39",
    cadence: "once",
    desc: "Three teams, full ride through October.",
    cadenceKey: "seasonal",
    popular: true,
  },
];

function DoubleRule({ className = "" }) {
  return (
    <div role="presentation" aria-hidden="true" className={className}>
      <div className="h-[3px] bg-brand-black" />
      <div className="mt-[3px] h-px bg-brand-red" />
    </div>
  );
}

function RedTick() {
  return (
    <span
      aria-hidden="true"
      className="mt-2 inline-block h-1.5 w-1.5 bg-brand-red shrink-0"
    />
  );
}

function Hero() {
  return (
    <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 px-6 overflow-hidden bsc-grain">
      <img
        src="/brand/hero-bg-stadium.jpg"
        alt=""
        aria-hidden="true"
        draggable="false"
        className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover object-center opacity-[0.13]"
        data-testid="hero-bg-stadium"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-brand-paper"
      />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* LEFT: editorial copy */}
          <div className="lg:col-span-7 text-left">
            <FadeUp>
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="h-px w-10 bg-brand-red" />
                <p className="font-mono text-[11px] font-semibold tracking-[0.28em] uppercase text-brand-red">
                  The Daily MLB Briefing
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={120}>
              <h1
                className="mt-7 font-serif font-black text-[1.9rem] sm:text-[2.55rem] lg:text-[3.2rem] text-brand-black leading-[1.08] tracking-[-0.01em]"
                data-testid="hero-headline"
              >
                For fans who{" "}
                <span className="italic text-brand-red">work too much</span> to
                keep up.
              </h1>
            </FadeUp>

            <FadeUp delay={240}>
              <p className="mt-6 max-w-xl text-base md:text-lg text-brand-muted-strong leading-relaxed">
                Work comes first. We get it. The Box Score Club turns last
                night's games into a five-minute morning email briefing, so
                you never fall behind on your team.
              </p>
            </FadeUp>

            <FadeUp delay={360}>
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/free-signup"
                  className="inline-flex items-center justify-center gap-2 bg-brand-black text-white px-6 py-3.5 font-semibold text-[0.9rem] border border-brand-black shadow-hard-sm hover:bg-brand-red hover:border-brand-red transition-colors duration-200"
                  data-testid="hero-cta-free"
                >
                  Get the Free Weekly
                  <span aria-hidden="true" className="-mr-0.5">
                    →
                  </span>
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 bg-transparent text-brand-black border border-brand-black px-6 py-3.5 font-semibold text-[0.9rem] hover:text-brand-red hover:border-brand-red transition-colors duration-200"
                  data-testid="hero-cta-paid"
                >
                  Get Daily for $1.99/week
                </Link>
              </div>
              <p className="mt-5 font-mono text-xs tracking-[0.08em] text-brand-muted">
                Start free, no card. Upgrade whenever.
              </p>
            </FadeUp>
          </div>

          {/* RIGHT: club mark on a collector's-card panel */}
          <FadeUp delay={180} className="lg:col-span-5">
            <div
              className="relative bg-white border border-brand-black shadow-hard p-6 md:p-8 max-w-md mx-auto lg:max-w-none"
              data-testid="hero-logo-panel"
            >
              <img
                src="/brand/logo-hero.png"
                alt="The Box Score Club"
                width={1536}
                height={1024}
                loading="eager"
                fetchPriority="high"
                draggable="false"
                className="block w-full h-auto select-none"
              />
              <div className="mt-4 pt-3.5 border-t border-brand-black/15 flex items-center justify-end font-mono text-[9px] font-semibold tracking-[0.24em] uppercase">
                <span className="text-brand-red">Est. 2026</span>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={420}>
          <div className="mt-12 md:mt-16 flex justify-center">
            <Link
              to="/lineup-card"
              className="group inline-flex items-center justify-center gap-2.5 bg-brand-red border border-brand-red text-white px-6 py-3.5 font-mono text-[11px] font-bold tracking-[0.22em] uppercase hover:bg-brand-red-hover hover:border-brand-red-hover transition-colors duration-200 w-full sm:w-auto"
              data-testid="hero-link-lineup-card"
            >
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 bg-white animate-pulse-soft" />
              Play the Lineup Card
              <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-0.5">
                →
              </span>
            </Link>
          </div>
        </FadeUp>

        <DoubleRule className="mt-6 md:mt-8" />
      </div>
    </section>
  );
}

function Scoreboard() {
  const Game = ({ team, score, winner }) => (
    <div
      className="flex items-baseline gap-3"
      data-testid={`scoreboard-${team.toLowerCase()}`}
    >
      {winner && (
        <span aria-hidden="true" className="text-brand-red text-xs font-mono">
          ▸
        </span>
      )}
      <span
        className={`font-mono text-xs font-semibold tracking-[0.18em] ${
          winner ? "text-brand-black" : "text-brand-muted"
        }`}
      >
        {team}
      </span>
      <span
        className={`font-mono text-2xl tabular-nums leading-none ${
          winner
            ? "font-bold text-brand-black"
            : "font-medium text-brand-muted-strong"
        }`}
      >
        {score}
      </span>
    </div>
  );

  return (
    <div className="flex items-center gap-6 md:gap-10">
      <Game team="PHI" score="4" winner />
      <span aria-hidden="true" className="font-mono text-xs text-brand-muted-soft">
        F
      </span>
      <Game team="CIN" score="1" />
    </div>
  );
}

function SampleRecap() {
  return (
    <section id="sample" className="relative py-8 md:py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <FadeUp>
          <div className="bg-white border border-brand-black shadow-mockup overflow-hidden">
            <div className="px-6 py-4 border-b border-brand-black/15 bg-brand-surface/60">
              <div className="flex items-center justify-between mb-2.5">
                <span className="font-mono text-[10px] font-semibold tracking-[0.22em] uppercase text-brand-muted">
                  Inbox
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block w-1.5 h-1.5 bg-brand-red"
                />
              </div>
              <p className="font-mono text-xs text-brand-muted">
                <span className="text-brand-muted-strong">From:</span> The Box
                Score Club &lt;recap@theboxscoreclub.com&gt;
              </p>
              <p className="mt-1.5 font-serif text-lg font-bold text-brand-black">
                Wheeler dealt 14 Ks, no walks
              </p>
              <p className="mt-0.5 font-mono text-[11px] text-brand-muted">
                Tuesday, July 7 · 7:02am
              </p>
            </div>
            <div className="px-6 md:px-9 py-7 md:py-8">
              <div className="pb-5 mb-5 border-b border-brand-black/10">
                <Scoreboard />
              </div>
              <p className="text-base md:text-lg text-brand-ink leading-relaxed">
                Wheeler carved up Cincinnati with a career-high-tying 14
                strikeouts and didn't walk a soul over seven innings. Schwarber
                launched a two-run shot in the third off Abbott, and that was
                all Phillies needed. Suárez yanked a solo bomb in the seventh
                for Reds' only run.
              </p>
            </div>
          </div>
        </FadeUp>
        <FadeUp delay={120}>
          <p className="mt-6 text-center font-mono text-xs tracking-[0.06em] text-brand-muted">
            An actual recap from yesterday. Not a placeholder.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

function PlusTheBriefing() {
  return (
    <section className="py-16 md:py-24 px-6">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-5">
          <FadeUp>
            <span className="inline-block border border-brand-red text-brand-red font-mono text-[10px] font-semibold tracking-[0.22em] uppercase px-3 py-1.5">
              Included
            </span>
          </FadeUp>
          <FadeUp delay={80}>
            <h2 className="mt-6 font-serif font-black text-display-md text-brand-black leading-tight">
              The whole league, before your first meeting.
            </h2>
          </FadeUp>
          <FadeUp delay={160}>
            <p className="mt-5 text-base md:text-lg text-brand-muted-strong leading-relaxed">
              Every email links to the full daily briefing. League-wide
              standings, last night's headlines, transactions, injury wire,
              trend watch, and deep dives on your teams. Skim it in the
              elevator and sound like you watched everything.
            </p>
          </FadeUp>
          <FadeUp delay={240}>
            <ul className="mt-7 space-y-3 text-sm text-brand-muted-strong">
              {[
                "All 6 divisions at a glance",
                "5 headlines from around the league",
                "Roster moves and IL updates",
                "Deep dive per team with Edge analysis",
                "Opens right in your browser, loads in a second",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <RedTick />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
        <FadeUp className="md:col-span-7" delay={200}>
          <BriefingMock />
        </FadeUp>
      </div>
    </section>
  );
}

function BriefingMock() {
  return (
    <div
      className="bg-white border border-brand-black shadow-hard overflow-hidden"
      data-testid="briefing-mock"
    >
      {/* Masthead */}
      <div className="bg-brand-black px-5 py-4 flex items-end justify-between border-b-[3px] border-brand-red">
        <div>
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/60">
            Your Daily MLB Briefing
          </p>
          <p className="mt-1 text-white text-lg font-black tracking-tight font-serif">
            The Box Score Club
          </p>
        </div>
        <p className="font-mono text-[9px] tracking-[0.14em] uppercase text-white/60">
          Apr 28, 2026
        </p>
      </div>

      {/* Body grid mock */}
      <div className="p-5 space-y-4">
        {/* Standings grid (3x2) */}
        <div>
          <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-brand-red font-semibold mb-2">
            League Standings
          </p>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-brand-black/10 p-2.5">
                <div className="h-1.5 w-10 bg-brand-red" />
                <div className="mt-2 space-y-1.5">
                  <div className="h-1.5 bg-brand-surface w-full" />
                  <div className="h-1.5 bg-brand-surface w-11/12" />
                  <div className="h-1.5 bg-brand-surface w-10/12" />
                  <div className="h-1.5 bg-brand-surface w-9/12" />
                  <div className="h-1.5 bg-brand-surface w-8/12" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Headlines (2/3) + Transactions (1/3) */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 border border-brand-black/10 p-3">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-brand-red font-semibold">
              Last Night's Headlines
            </p>
            <div className="mt-2 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="mt-1 h-1 w-1 bg-brand-red shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-1.5 bg-brand-rule w-full" />
                    <div className="h-1.5 bg-brand-surface w-11/12" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-brand-black/10 p-3">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-brand-red font-semibold">
              Transactions
            </p>
            <div className="mt-2 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-1">
                  <div className="h-1.5 bg-brand-surface w-full" />
                  <div className="h-1.5 bg-brand-surface w-10/12" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Power movement */}
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-brand-black px-3 py-3 flex flex-col">
            <span className="font-mono text-[10px] font-bold text-white">
              UP +5
            </span>
            <span className="mt-auto font-serif text-base font-black text-white">
              NYY
            </span>
          </div>
          <div className="bg-brand-black px-3 py-3 flex flex-col">
            <span className="font-mono text-[10px] font-bold text-white/80">
              UP +3
            </span>
            <span className="mt-auto font-serif text-base font-black text-white">
              BOS
            </span>
          </div>
          <div className="bg-brand-red px-3 py-3 flex flex-col">
            <span className="font-mono text-[10px] font-bold text-white">
              DOWN -2
            </span>
            <span className="mt-auto font-serif text-base font-black text-white">
              CWS
            </span>
          </div>
          <div className="bg-brand-red-hover px-3 py-3 flex flex-col">
            <span className="font-mono text-[10px] font-bold text-white">
              DOWN -4
            </span>
            <span className="mt-auto font-serif text-base font-black text-white">
              COL
            </span>
          </div>
        </div>

        {/* Deep dive banner mock */}
        <div className="border border-brand-black/10 overflow-hidden">
          <div className="bg-brand-black px-4 py-2.5 flex items-end justify-between border-b-2 border-brand-red">
            <span className="font-serif text-sm font-black text-white tracking-wide">
              YANKEES
            </span>
            <span className="font-mono text-[9px] text-white/60">
              20-10 · 1st AL East
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 p-3">
            {["LAST GAME", "NEWS", "NEXT UP"].map((label) => (
              <div key={label}>
                <p className="font-mono text-[8px] font-semibold tracking-[0.2em] uppercase text-brand-red">
                  {label}
                </p>
                <div className="mt-1.5 space-y-1">
                  <div className="h-1 bg-brand-surface w-full" />
                  <div className="h-1 bg-brand-surface w-11/12" />
                  <div className="h-1 bg-brand-surface w-9/12" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FreeVsDaily() {
  return (
    <section
      id="free-or-daily"
      className="py-16 md:py-24 px-6"
      data-testid="free-or-daily-section"
    >
      <div className="max-w-6xl mx-auto">
        <FadeUp>
          <p className="font-mono text-[11px] font-semibold tracking-[0.28em] text-brand-red uppercase">
            Free or Daily
          </p>
          <h2 className="mt-4 font-serif font-black text-display-md text-brand-black max-w-2xl leading-tight">
            Two ways to stay in the loop.
          </h2>
        </FadeUp>

        <div className="mt-11 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* FREE WEEKLY */}
          <FadeUp>
            <div
              className="relative h-full bg-white border border-brand-black/20 p-7 md:p-9 flex flex-col"
              data-testid="free-card"
            >
              <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-brand-red">
                Free Weekly
              </p>
              <h3 className="mt-4 font-serif text-xl md:text-[1.6rem] font-black text-brand-black">
                The Friday Briefing
              </h3>
              <ul className="mt-7 space-y-3.5 text-base text-brand-muted-strong">
                {[
                  "Every Friday morning",
                  "The week's five biggest stories",
                  "League-wide standings",
                  "Free forever, no card",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <RedTick />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-9">
                <Link
                  to="/free-signup"
                  className="block w-full text-center bg-brand-black text-white px-6 py-4 font-semibold text-base border border-brand-black hover:bg-brand-red hover:border-brand-red transition-colors duration-200"
                  data-testid="free-card-cta"
                >
                  Sign Up Free
                </Link>
              </div>
            </div>
          </FadeUp>

          {/* DAILY — the black card */}
          <FadeUp delay={120}>
            <div
              className="relative h-full bg-brand-black text-white p-7 md:p-9 flex flex-col shadow-hard-red-sm border-t-[3px] border-brand-red"
              data-testid="daily-card"
            >
              <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-brand-red">
                Daily
              </p>
              <h3 className="mt-4 font-serif text-xl md:text-[1.6rem] font-black text-white">
                The Full Daily
              </h3>
              <ul className="mt-7 space-y-3.5 text-base text-white/75">
                {[
                  "Your team, every single morning",
                  "Reads in five minutes with coffee",
                  "Full daily briefing included",
                  "1 team or 3 teams",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <RedTick />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 font-mono text-sm text-white/50">
                $1.99/week or $25/season
              </p>
              <div className="mt-auto pt-7">
                <Link
                  to="/signup"
                  className="block w-full text-center bg-brand-red text-white px-6 py-4 font-semibold text-base hover:bg-brand-paper hover:text-brand-black transition-colors duration-200"
                  data-testid="daily-card-cta"
                >
                  Get Daily
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      h: "Pick your teams",
      d: "Choose 1 or 3 teams. All 30 covered.",
    },
    {
      n: "02",
      h: "Pay once or weekly",
      d: "$25 for the season or $1.99 per week. Comp codes for friends.",
    },
    {
      n: "03",
      h: "Read it with coffee",
      d: "Lands every morning during the season. Five minutes and you can talk about any game like you stayed up for it.",
    },
  ];
  return (
    <section className="py-16 md:py-24 px-6 bg-brand-surface border-y border-brand-black/10">
      <div className="max-w-6xl mx-auto">
        <FadeUp>
          <p className="font-mono text-[11px] font-semibold tracking-[0.28em] text-brand-red uppercase">
            How it works
          </p>
          <h2 className="mt-4 font-serif font-black text-display-md text-brand-black max-w-2xl leading-tight">
            Three steps. No onboarding wizard.
          </h2>
        </FadeUp>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {steps.map((s, i) => (
            <FadeUp key={s.n} delay={i * 120}>
              <div className="flex flex-col">
                <span
                  aria-hidden="true"
                  className="font-serif text-6xl font-black text-transparent [-webkit-text-stroke:1.5px_#C62C29] leading-none tabular-nums"
                  data-testid={`step-tag-${s.n}`}
                >
                  {s.n}
                </span>
                <h3 className="mt-5 font-serif text-lg md:text-xl font-black text-brand-black">
                  {s.h}
                </h3>
                <p className="mt-3 text-base text-brand-muted-strong leading-relaxed max-w-xs">
                  {s.d}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ plan, label, price, cadence, desc, popular, cadenceKey }) {
  const isSeasonal = cadenceKey === "seasonal";
  return (
    <FadeUp className="h-full">
      <div
        className={`relative h-full bg-white border p-6 md:p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 flex flex-col ${
          popular
            ? "border-brand-red shadow-hard-red-sm hover:shadow-hard-red"
            : "border-brand-black/20 shadow-soft hover:shadow-card-hover"
        }`}
        data-testid={`pricing-card-${plan}`}
      >
        {popular && (
          <span className="absolute -top-3.5 right-6 inline-block bg-brand-red text-white font-mono text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5">
            Most popular
          </span>
        )}
        <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-brand-muted uppercase">
          {label}
        </p>

        <p className="mt-5 font-mono text-[10px] font-semibold tracking-[0.22em] text-brand-muted uppercase">
          Price
        </p>
        <div className="mt-1.5 h-px w-full bg-brand-black/10" />
        <div className="mt-3 flex items-baseline gap-2">
          <span
            className={`font-serif text-3xl md:text-4xl tracking-tight tabular-nums ${
              isSeasonal
                ? "font-black text-brand-black"
                : "font-bold text-brand-muted-strong"
            }`}
            data-testid={`pricing-amount-${plan}`}
          >
            {price}
          </span>
          <span className="text-sm text-brand-muted">{cadence}</span>
        </div>
        <p className="mt-3.5 text-sm text-brand-muted-strong leading-relaxed">
          {desc}
        </p>
        <Link
          to={`/signup?plan=${plan}`}
          className={`mt-6 w-full inline-flex items-center justify-center gap-1.5 py-3.5 font-semibold text-sm transition-colors duration-200 ${
            popular
              ? "bg-brand-red text-white hover:bg-brand-black"
              : "bg-brand-black text-white hover:bg-brand-red"
          }`}
          data-testid={`pricing-cta-${plan}`}
        >
          Choose this plan
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </FadeUp>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-16 md:py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <FadeUp>
          <p className="font-mono text-[11px] font-semibold tracking-[0.28em] text-brand-red uppercase">
            Pricing
          </p>
          <h2 className="mt-4 font-serif font-black text-display-md text-brand-black max-w-2xl leading-tight">
            Pick a plan that fits how you follow.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-brand-muted-strong leading-relaxed">
            All paid plans include the full daily briefing and your teams'
            daily recaps. Billing pauses when the season ends, so you never
            pay for months without games.
          </p>
        </FadeUp>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
          {PRICING.map((p) => (
            <PricingCard key={p.plan} {...p} />
          ))}
        </div>
        <FadeUp>
          <p className="mt-9 text-sm text-brand-muted italic">
            Cancel anytime. No refunds. Friend?{" "}
            <a
              href="mailto:clubhouse@theboxscoreclub.com"
              className="text-brand-red underline underline-offset-4 hover:text-brand-red-hover transition-colors not-italic"
            >
              clubhouse@theboxscoreclub.com
            </a>{" "}
            for a comp code.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="py-20 md:py-28 px-6 bg-brand-paper relative">
      <div className="max-w-3xl mx-auto text-center">
        <FadeUp>
          <SeamDivider className="mb-10" />
          <h2 className="font-serif font-black text-display-lg text-brand-black leading-[1.08]">
            Five minutes. Every morning.{" "}
            <span className="italic text-brand-red">Never out of the loop.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={120}>
          <div className="mt-10">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-4 font-semibold text-base border border-brand-red shadow-hard-sm hover:bg-brand-black hover:border-brand-black transition-colors duration-200"
              data-testid="final-cta"
            >
              Pick Your Teams
              <span aria-hidden="true" className="-mr-0.5">
                →
              </span>
            </Link>
          </div>
          <p className="mt-5 font-mono text-xs tracking-[0.08em] text-brand-muted">
            $1.99/week or $25 for the season.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="bg-brand-paper text-brand-black">
      <BaseballCursor />
      <Nav />
      <main>
        <Hero />
        <SampleRecap />
        <PlusTheBriefing />
        <div className="px-6 py-3 md:py-5">
          <SeamDivider />
        </div>
        <FreeVsDaily />
        <HowItWorks />
        <Pricing />
        <FinalCta />
      </main>
      <SiteFooter />
    </div>
  );
}
